import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckIcon from "@mui/icons-material/Check";
import CloseIconOutlined from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import LeadsMobileStatusChip, { LeadsMobileBrandChip } from "./LeadsMobileStatusChip";
import {
  STATUS_META,
  formatLeadDateTime,
  formatCurrency,
  formatDuration,
  getQualifiedMeta,
  SCORING_STATUS_META,
  REPORT_LOG_STATUS_META,
} from "../constants";
import { parseRationaleBlocks, toRationaleLines } from "../Leads/LeadDetailDrawer";
import { formatAcquiredDate, formatAcquiredTime } from "../Leads/LeadsTable";
import { getMobileScoreMeta } from "./mobileScoreMeta";
import { colors, spacing, typography } from "../../../../assets/styles/tokens";

interface LeadDetailDrawerMobileProps {
  data: LeadsManagementData;
}

type DetailTabKey = "details" | "scoring" | "log";

function formatShortDate(iso: string): string {
  const date = new Date(iso);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

// Mobile-only variant of the shared LeadDetailDrawer (Leads/LeadDetailDrawer.tsx)
// — a full-screen Dialog with an X + centered title instead of a side
// Drawer, and token-driven colors/typography throughout. Reuses the same
// data shape (sections/fields, scoring rationale, report log) as the
// desktop version rather than duplicating that logic.
function DetailAccordion({
  title,
  headerRight,
  subtitle,
  defaultExpanded,
  children,
}: {
  title: string;
  headerRight?: ReactNode;
  subtitle?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${colors.GrayLightMode[200].$value}`,
        borderRadius: `${spacing.Radius.lg.$value} !important`,
        overflow: "hidden",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: spacing.Spacing.md.$value,
          minHeight: subtitle ? "56px !important" : "40px !important",
          "& .MuiAccordionSummary-content": { my: spacing.Spacing.md1.$value },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: spacing.Spacing.md1.$value }}>
          <Box>
            <Typography
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.sm.$value,
                color: colors.GrayLightMode[900].$value,
              }}
            >
              {title}
            </Typography>
            {subtitle && <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.sm.$value, mt: spacing.Spacing.xs.$value }}>{subtitle}</Box>}
          </Box>
          {headerRight}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: spacing.Spacing.md.$value, pt: 0, pb: spacing.Spacing.md1.$value }}>{children}</AccordionDetails>
    </Accordion>
  );
}

// Long values like the ad platform click ID wrap into an unreadable block on
// mobile, so they're shown as a one-tap copy action instead of inline text.
function CopyValueButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const handle = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(handle);
  }, [copied]);

  return (
    <Box
      component="span"
      onClick={() => navigator.clipboard.writeText(value).then(() => setCopied(true))}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: spacing.Spacing.xs.$value,
        cursor: "pointer",
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Regular.$value,
        fontSize: typography.Size.Text.xs.$value,
        lineHeight: typography.Line_height.text.xs.$value,
        color: colors.Brand[600].$value,
      }}
    >
      <ContentCopyIcon sx={{ fontSize: 16 }} />
      {copied ? "Copied" : "Copy Link"}
    </Box>
  );
}

function SectionAccordion({
  title,
  fields,
  defaultExpanded,
}: {
  title: string;
  fields: Array<[string, ReactNode]>;
  defaultExpanded?: boolean;
}) {
  return (
    <DetailAccordion title={title} defaultExpanded={defaultExpanded}>
      {fields.map(([label, value], index) => (
        <Box
          key={label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: spacing.Spacing.lg.$value,
            py: spacing.Spacing.xs.$value,
            borderTop: index === 0 ? "none" : `1px solid ${colors.GrayLightMode[200].$value}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xs.$value,
              color: colors.GrayLightMode[700].$value,
            }}
          >
            {label}
          </Typography>
          <Typography
            component="div"
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.sm.$value,
              color: colors.GrayLightMode[900].$value,
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </DetailAccordion>
  );
}

function EmptyState({ icon, message }: { icon: ReactNode; message: string }) {
  return (
    <Box
      sx={{
        bgcolor: colors.Base.White.$value,
        border: `1px solid ${colors.GrayLightMode[200].$value}`,
        borderRadius: spacing.Radius.lg.$value,
        p: "44px 20px",
        textAlign: "center",
      }}
    >
      {icon}
      <Typography sx={{ fontSize: 14, color: colors.GrayLightMode[500].$value, mt: spacing.Spacing.md.$value }}>{message}</Typography>
    </Box>
  );
}

export default function LeadDetailDrawerMobile({ data }: LeadDetailDrawerMobileProps) {
  const {
    detailLead,
    closeLeadDetail,
    requestReportLead,
    isReporting,
    canReportLeads,
    goToFilteredLeads,
    requestRejectLead,
  } = data;
  const [activeTab, setActiveTab] = useState<DetailTabKey>("details");

  useEffect(() => {
    setActiveTab("details");
  }, [detailLead?.id]);

  if (!detailLead) return null;

  const meta = STATUS_META[detailLead.status];
  const qualifiedMeta = getQualifiedMeta(detailLead.qualifiedByBrand);


  const isBrandDuplication = detailLead.status === "BrandDuplication";
  const reportOutcomeMessage =
    detailLead.status === "Success"
      ? { text: "Reported Successfully to Brand", color: colors.Success[600].$value }
      : detailLead.status === "Failure" || isBrandDuplication
      ? { text: "This lead cannot be reported.", color: colors.Error[600].$value }
      : detailLead.status === "Rejected"
      ? { text: "Rejected", color: colors.Error[600].$value }
      : null;

  const sections: Array<{ title: string; fields: Array<[string, ReactNode]> }> = [
    {
      title: "Brand Outcome",
      fields: [
        ["BRAND LEAD STATUS", detailLead.brandLeadStatus ?? "-"],
        ["QUALIFIED BY BRAND", qualifiedMeta.label],
        ["INCOME FROM BRAND", formatCurrency(detailLead.incomeFromBrand)],
        ["REPORTED AT", formatLeadDateTime(detailLead.reportedAt)],
        ["REPORT ATTEMPTS", String(detailLead.reportLog.length)],
      ],
    },
    {
      title: "Contact",
      fields: [
        ["FULL NAME", detailLead.name],
        ["EMAIL", detailLead.email],
        ["PHONE", detailLead.phone],
        ["ZIP CODE", detailLead.zip],
      ],
    },
    {
      title: "Business & Loan",
      fields: [
        ["BUSINESS NAME", detailLead.businessName],
        ["BUSINESS INDUSTRY", detailLead.industry],
        ["TIME IN BUSINESS", detailLead.timeInBusiness],
        ["MONTHLY REVENUE", detailLead.revenue],
        ["CREDIT RATING", detailLead.credit],
        ["BORROW AMOUNT", detailLead.amount],
        ["LOAN PURPOSE", detailLead.loanPurpose],
      ],
    },
    {
      title: "Attribution",
      fields: [
        ["USER UNIQUE ID", detailLead.userUniqueId],
        ["TRAFFIC SOURCE", detailLead.trafficSource],
        ["AD ID", detailLead.adId],
        [
          "AD PLATFORM CLICK ID",
          detailLead.adPlatformClickId ? <CopyValueButton value={detailLead.adPlatformClickId} /> : "—",
        ],
        ["DATE OF ACQUISITION", formatLeadDateTime(detailLead.date)],
      ],
    },
  ];

  const ipDupes = detailLead.duplicateIpLeads;

  const scoringStatus = detailLead.scoringStatus;
  const hasScoring =
    scoringStatus === "completed" && detailLead.leadScore !== null && detailLead.approvalLikelihood !== null;
  const noScoringMessage =
    scoringStatus === "processing"
      ? "Scoring is still processing for this lead — check back shortly."
      : scoringStatus === "failed"
        ? "The scoring run failed for this lead. No score is available."
        : "No scoring data is available for this lead yet.";

  const reportLog = detailLead.reportLog;

  return (
    <Dialog open fullScreen onClose={closeLeadDetail}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: spacing.Spacing.xl.$value,
          py: spacing.Spacing.lg.$value,
          borderBottom: `1px solid ${colors.GrayLightMode[200].$value}`,
        }}
      >
        <IconButton size="small" onClick={closeLeadDetail}>
          <CloseIcon fontSize="small" sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            fontSize: typography.Size.Text.md.$value,
            color: colors.GrayLightMode[900].$value,
          }}
        >
          Lead Information
        </Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box sx={{ px: spacing.Spacing.xl.$value, pt: spacing.Spacing.lg.$value }}>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            fontSize: typography.Size.Text.sm.$value,
            color: colors.GrayLightMode[900].$value,
            textTransform: "capitalize",
          }}
        >
          {detailLead.name}{" "}
          <Typography
            component="span"
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Regular.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: colors.GrayLightMode[500].$value,
            }}
          >
            ({detailLead.id})
          </Typography>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: spacing.Spacing.md.$value, mt: spacing.Spacing.xxs.$value }}>
          <Typography
            sx={{
              fontSize: 13,
              color: colors.GrayLightMode[500].$value,
              maxWidth: "200px",
              wordBreak: "break-word",
            }}
          >
            {detailLead.email}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.md.$value }}>
            <LeadsMobileStatusChip label={meta.label} />
            <LeadsMobileStatusChip label={qualifiedMeta.label} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: spacing.Spacing.md.$value, mt: spacing.Spacing.md.$value }}>
          <Typography
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              letterSpacing: "0%",
              color: colors.GrayLightMode[900].$value,
            }}
          >
            {detailLead.brand}
          </Typography>
          <Box sx={{ width: "1px", height: 14, bgcolor: colors.GrayLightMode[200].$value }} />
          <Typography
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: colors.GrayLightMode[700].$value,
            }}
          >
            INCOME
          </Typography>
          <Typography
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              letterSpacing: "0%",
              color: detailLead.incomeFromBrand ? colors.Success[600].$value : colors.GrayLightMode[500].$value,
            }}
          >
            {formatCurrency(detailLead.incomeFromBrand)}
          </Typography>
        </Box>

        {reportOutcomeMessage && !isBrandDuplication && (
          <Typography sx={{ fontSize: 13, mt: spacing.Spacing.md.$value, color: reportOutcomeMessage.color }}>
            {reportOutcomeMessage.text}
          </Typography>
        )}

        {detailLead.status !== "Failure" && detailLead.status !== "Success" && detailLead.status !== "Rejected" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isBrandDuplication ? "space-between" : "flex-start",
              gap: spacing.Spacing.md.$value,
              mt: spacing.Spacing.lg.$value,
            }}
          >
            <Button
              variant="text"
              size="medium"
              disabled={!canReportLeads}
              onClick={() => requestRejectLead(detailLead.id)}
              sx={{
                // No left padding/min-width so the label lines up with the
                // content's left edge above.
                pl: 0,
                minWidth: 0,
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.xs.$value,
                lineHeight: typography.Line_height.text.xs.$value,
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: colors.Brand[700].$value,
                "&:hover": { backgroundColor: colors.Brand[25].$value },
              }}
            >
              Reject
            </Button>
            {/* Brand Duplication can't be reported, so its message takes the
                "Report to brand" button's place beside Reject. */}
            {isBrandDuplication && reportOutcomeMessage && (
              <Typography sx={{ fontSize: 13, color: reportOutcomeMessage.color }}>{reportOutcomeMessage.text}</Typography>
            )}
            {!isBrandDuplication && (
              <Button
                variant="contained"
                size="medium"
                disabled={!canReportLeads || isReporting}
                onClick={() => requestReportLead(detailLead.id)}
                sx={{
                  boxShadow: "none",
                  bgcolor: colors.Brand[600].$value,
                  "&:hover": { bgcolor: colors.Brand[700].$value },
                  // Keep the in-progress state readable — MUI's disabled grey text is illegible on the brand fill.
                  ...(isReporting && {
                    "&.Mui-disabled": { color: colors.Base.White.$value, bgcolor: colors.Brand[600].$value },
                  }),
                  fontFamily: typography.Font_Family.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  fontSize: typography.Size.Text.xs.$value,
                  lineHeight: typography.Line_height.text.xs.$value,
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                }}
              >
                {isReporting ? "Reporting…" : "Report to brand"}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: spacing.Spacing.lg.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}` }} />

      <Box sx={{ px: spacing.Spacing.xl.$value, mt: spacing.Spacing.lg.$value }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="fullWidth"
          sx={{
            minHeight: 0,
            bgcolor: colors.GrayLightMode[100].$value,
            borderRadius: spacing.Radius.lg.$value,
            p: spacing.Spacing.xs.$value,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 0,
              py: spacing.Spacing.xs.$value,
              borderRadius: spacing.Radius.md.$value,
              fontSize: 13,
              fontWeight: 500,
              textTransform: "none",
              color: colors.GrayLightMode[500].$value,
            },
            "& .Mui-selected": {
              color: `${colors.GrayLightMode[900].$value} !important`,
              bgcolor: colors.Base.White.$value,
            },
          }}
        >
          <Tab value="details" label="Details" />
          <Tab value="scoring" label="Scoring" />
          <Tab value="log" label="Report Log" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: spacing.Spacing.xl.$value, py: spacing.Spacing.xl.$value }}>
        {activeTab === "details" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.lg.$value }}>
            {sections.map((section, index) => (
              <SectionAccordion key={section.title} title={section.title} fields={section.fields} defaultExpanded={index === 0} />
            ))}

            <DetailAccordion title="IP Address">
              <Box sx={{ display: "flex", justifyContent: "space-between", py: spacing.Spacing.xs.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}` }}>
                <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[700].$value }}>IP ADDRESS</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.GrayLightMode[900].$value }}>
                  {detailLead.ipAddress ?? "—"}
                </Typography>
              </Box>
              {ipDupes.length > 0 ? (
                <Box sx={{ pt: spacing.Spacing.lg.$value, mt: spacing.Spacing.xxs.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}` }}>
                  <Typography sx={{ fontSize: 12.5, color: colors.GrayLightMode[500].$value, mb: spacing.Spacing.md.$value }}>
                    {ipDupes.length} other lead{ipDupes.length === 1 ? "" : "s"} submitted from this IP:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.md.$value }}>
                    {ipDupes.map((dup) => (
                      <Box
                        key={dup.id}
                        onClick={() => goToFilteredLeads({ search: String(dup.id) })}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                          border: `1px solid ${colors.Brand[100].$value}`,
                          bgcolor: colors.Brand[25].$value,
                          borderRadius: spacing.Radius.md.$value,
                          px: spacing.Spacing.lg.$value,
                          py: spacing.Spacing.md.$value,
                          cursor: "pointer",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.Brand[700].$value }}>
                          #{dup.id}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: colors.GrayLightMode[700].$value, flex: 1, minWidth: 0 }}>
                          {dup.fullName}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: colors.GrayLightMode[500].$value }}>
                          {formatShortDate(dup.created)}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 15, color: colors.Brand[700].$value }} />
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    onClick={() => detailLead.ipAddress && goToFilteredLeads({ search: detailLead.ipAddress })}
                    sx={{ fontSize: 12.5, fontWeight: 500, color: colors.Brand[700].$value, cursor: "pointer", mt: spacing.Spacing.md1.$value, textAlign: "right" }}
                  >
                    See all leads from this IP
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ pt: spacing.Spacing.lg.$value, mt: spacing.Spacing.xxs.$value, borderTop: `1px solid ${colors.GrayLightMode[200].$value}`, fontSize: 12.5, color: colors.GrayLightMode[500].$value }}>
                  No other lead was submitted from this IP address.
                </Typography>
              )}
            </DetailAccordion>
          </Box>
        )}

        {activeTab === "scoring" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.lg.$value }}>
            {hasScoring ? (
              [
                { title: "Lead Scoring Rationale", score: detailLead.leadScore!, rationale: detailLead.scoreRationale },
                {
                  title: "Loan Approval Likelihood  Rationale",
                  score: detailLead.approvalLikelihood!,
                  rationale: detailLead.approvalRationale,
                },
              ].map(({ title, score, rationale }) => {
                const scoreMeta = getMobileScoreMeta(score);
                const blocks = parseRationaleBlocks(rationale);
                return (
                  <DetailAccordion
                    key={title}
                    title={title}
                    defaultExpanded={title === "Lead Scoring Rationale"}
                    subtitle={
                      <>
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 600,
                            fontSize: "10px",
                            lineHeight: "14px",
                            letterSpacing: "0%",
                            color: scoreMeta.color,
                          }}
                        >
                          {Number.isInteger(score) ? score : score.toFixed(1)}/10
                        </Typography>
                        <Box
                          sx={{
                            px: spacing.Spacing.md.$value,
                            py: spacing.Spacing.xxs.$value,
                            borderRadius: spacing.Radius.xs.$value,
                            bgcolor: scoreMeta.bg,
                            color: scoreMeta.color,
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {scoreMeta.label}
                        </Box>
                      </>
                    }
                  >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.lg1.$value, pt: spacing.Spacing.xs.$value }}>
                      {blocks.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: colors.GrayLightMode[500].$value }}>
                          No rationale available.
                        </Typography>
                      ) : (
                        blocks.map((block, index) => {
                          if (block.type === "bullet") {
                            const color = block.passed ? colors.Success[600].$value : colors.Error[600].$value;
                            const Icon = block.passed ? CheckIcon : CloseIconOutlined;
                            return (
                              <Box key={index}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: spacing.Spacing.sm.$value }}>
                                  <Icon sx={{ fontSize: 16, color, mt: "1px" }} />
                                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color }}>
                                    {block.title}
                                  </Typography>
                                </Box>
                                {block.body && (
                                  <Typography sx={{ fontSize: 12.5, color: colors.GrayLightMode[600].$value, mt: 0.375, ml: spacing.Spacing["3xl"].$value }}>
                                    {block.body}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }

                          if (block.type === "action") {
                            return (
                              <Box
                                key={index}
                                sx={{
                                  bgcolor: colors.Brand[50].$value,
                                  border: `1px solid ${colors.Brand[600].$value}`,
                                  borderRadius: spacing.Radius.md.$value,
                                  py: spacing.Spacing.md.$value,
                                  px: spacing.Spacing.xl.$value,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.sm.$value }}>
                                  <InfoIcon sx={{ fontSize: 16, color: colors.Brand[700].$value }} />
                                  <Typography
                                    sx={{
                                      fontFamily: typography.Font_Family.$value,
                                      fontWeight: typography.Font_weight.Medium.$value,
                                      fontSize: typography.Size.Text.xs.$value,
                                      lineHeight: typography.Line_height.text.xs.$value,
                                      letterSpacing: "0%",
                                      verticalAlign: "middle",
                                      color: colors.Brand[700].$value,
                                    }}
                                  >
                                    {block.title}
                                  </Typography>
                                </Box>
                                <Typography
                                  sx={{
                                    fontFamily: "Inter",
                                    fontWeight: 400,
                                    fontSize: "10px",
                                    lineHeight: "14px",
                                    letterSpacing: "0%",
                                    verticalAlign: "middle",
                                    color: colors.Brand[700].$value,
                                    mt: spacing.Spacing.xs.$value,
                                  }}
                                >
                                  {block.body}
                                </Typography>
                              </Box>
                            );
                          }

                          return (
                            <Typography
                              key={index}
                              component="div"
                              sx={{ fontSize: 13, color: colors.GrayLightMode[700].$value, whiteSpace: "pre-wrap" }}
                            >
                              {toRationaleLines(block.body).map(({ passed, text, indent }, lineIndex) => {
                                if (passed === null) {
                                  return (
                                    <Box key={lineIndex} sx={{ pl: indent ? "20px" : 0 }}>
                                      {text}
                                    </Box>
                                  );
                                }
                                const DotIcon = passed ? CheckIcon : CloseIconOutlined;
                                return (
                                  <Box key={lineIndex} sx={{ display: "flex", alignItems: "flex-start", gap: spacing.Spacing.xs.$value }}>
                                    <DotIcon
                                      sx={{
                                        fontSize: 16,
                                        mt: spacing.Spacing.xxs.$value,
                                        flexShrink: 0,
                                        color: passed ? colors.Success[600].$value : colors.Error[600].$value,
                                      }}
                                    />
                                    <span>{text}</span>
                                  </Box>
                                );
                              })}
                            </Typography>
                          );
                        })
                      )}
                    </Box>
                  </DetailAccordion>
                );
              })
            ) : (
              <EmptyState icon={<HourglassEmptyIcon sx={{ fontSize: 38, color: colors.Brand[200].$value }} />} message={noScoringMessage} />
            )}

            <SectionAccordion
              title="Scoring Run"
              fields={[
                ["STATUS", scoringStatus ? SCORING_STATUS_META[scoringStatus].label : "—"],
                [
                  "UPDATED",
                  detailLead.scoringUpdatedAt ? (
                    <>
                      {formatAcquiredDate(detailLead.scoringUpdatedAt)}
                      <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[500].$value }}>
                        {formatAcquiredTime(detailLead.scoringUpdatedAt)}
                      </Typography>
                    </>
                  ) : (
                    "—"
                  ),
                ],
                // "1m 21sec" per the design, vs the shared helper's "1m 21s".
                ["QUESTIONNAIRE FILL TIME", formatDuration(detailLead.questionnaireFillTimeSeconds).replace(/s$/, "sec")],
              ]}
            />
          </Box>
        )}

        {activeTab === "log" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.xl.$value }}>
            {reportLog.length === 0 ? (
              <EmptyState icon={<ReceiptLongIcon sx={{ fontSize: 38, color: colors.Brand[200].$value }} />} message="No report attempts recorded for this lead yet." />
            ) : (
              reportLog.map((log, index) => {
                const logMeta = REPORT_LOG_STATUS_META[log.status];
                return (
                  <DetailAccordion
                    key={log.id}
                    title={`Attempt #${log.attempt}`}
                    defaultExpanded={index === 0}
                    subtitle={
                      <>
                        <LeadsMobileBrandChip brand={log.brand} />
                        <LeadsMobileStatusChip label={logMeta.label} />
                      </>
                    }
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: spacing.Spacing.xs.$value }}>
                      <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[700].$value }}>PAYLOAD ID</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.GrayLightMode[900].$value }}>
                        Log ID {log.id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        py: spacing.Spacing.xs.$value,
                        borderTop: `1px solid ${colors.GrayLightMode[200].$value}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[700].$value }}>REPORTED ON</Typography>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.GrayLightMode[900].$value }}>
                          {formatAcquiredDate(log.createdAt)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[500].$value }}>
                          {formatAcquiredTime(log.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mt: spacing.Spacing.md.$value, "& span": { color: `${colors.GrayLightMode[600].$value} !important` } }}>
                      <SyntaxHighlighter
                        language="json"
                        style={materialLight}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{
                          margin: 0,
                          borderRadius: 8,
                          border: `1px solid ${colors.GrayLightMode[300].$value}`,
                          fontSize: 11,
                          maxHeight: 200,
                          overflow: "auto",
                          backgroundColor: colors.Base.White.$value,
                        }}
                      >
                        {log.payload}
                      </SyntaxHighlighter>
                    </Box>
                  </DetailAccordion>
                );
              })
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
